import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Button from "../components/UiElements/Button";
import Input from "../components/UiElements/Input";
import config from "../config";
import { tokens } from "../design-system/tokens";

type PendingCar = {
  carNumber: string;
  manufacturer: string | null;
  model: string | null;
  currentDriver: { name: string; phone: string };
};

const pageStyle: React.CSSProperties = {
  direction: "rtl",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  backgroundColor: tokens.colors.background,
};

const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 400,
  padding: 24,
  borderRadius: tokens.borderRadius.xl,
  backgroundColor: tokens.colors.surface,
  boxShadow: tokens.shadows.md,
};

async function publicRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${config.backendUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message || "Request failed");
  }
  return data as T;
}

const TestDriveStart: React.FC = () => {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [pendingCars, setPendingCars] = useState<PendingCar[]>([]);
  const [selectedCarNumber, setSelectedCarNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!phone.trim() || phone.replace(/\D/g, "").length < 9) {
      setPendingCars([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await publicRequest<{ cars: PendingCar[] }>(
          `/test-drive/pending-by-phone?phone=${encodeURIComponent(phone.trim())}`
        );
        setPendingCars(data.cars || []);
        if (data.cars?.length === 1) {
          setSelectedCarNumber(data.cars[0].carNumber);
          setName((prev) => prev || data.cars[0].currentDriver?.name || "");
        }
      } catch {
        setPendingCars([]);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [phone]);

  const submit = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      await publicRequest("/test-drive/approve", {
        method: "POST",
        body: JSON.stringify({
          phone: phone.trim(),
          name: name.trim() || undefined,
          carNumber: selectedCarNumber || undefined,
        }),
      });
      setDone(true);
      setMessage("נסיעת המבחן הופעלה בהצלחה!");
    } catch (err: any) {
      setError(err?.message || "שגיאה בהפעלת נסיעת המבחן");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <Typography sx={{ ...tokens.typography.h6, mb: 2, textAlign: "center" }}>
          התחלת נסיעת מבחן
        </Typography>
        {done ? (
          <Typography sx={{ ...tokens.typography.body, textAlign: "center" }}>{message}</Typography>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <Typography sx={{ ...tokens.typography.bodySmall, mb: 0.5 }}>טלפון</Typography>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                color="outline"
                size="medium"
                rtl
              />
            </div>
            {pendingCars.length > 1 ? (
              <div style={{ marginBottom: 16 }}>
                <Typography sx={{ ...tokens.typography.bodySmall, mb: 0.5 }}>בחר רכב</Typography>
                <select
                  value={selectedCarNumber}
                  onChange={(e) => {
                    setSelectedCarNumber(e.target.value);
                    const car = pendingCars.find((c) => c.carNumber === e.target.value);
                    if (car) setName(car.currentDriver?.name || "");
                  }}
                  style={{ width: "100%", padding: 10, borderRadius: 8 }}
                >
                  <option value="">בחר...</option>
                  {pendingCars.map((car) => (
                    <option key={car.carNumber} value={car.carNumber}>
                      {car.carNumber} — {car.model || car.manufacturer || ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div style={{ marginBottom: 16 }}>
              <Typography sx={{ ...tokens.typography.bodySmall, mb: 0.5 }}>שם (לאימות)</Typography>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                color="outline"
                size="medium"
                rtl
              />
            </div>
            {error ? (
              <Typography sx={{ color: tokens.colors.error, mb: 2, fontSize: 14 }}>{error}</Typography>
            ) : null}
            <Button
              label="התחל נסיעה"
              color="primary"
              size="medium"
              width="100%"
              onClick={submit}
              disabled={loading || !phone.trim()}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default TestDriveStart;
